import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Settings, Zap, ChevronDown, ChevronUp, Info, Save, Download, Code } from 'lucide-react';
import { stringify as stringifyYAML, parse as parseYAML } from 'yaml';
import { scriptsApi } from '@/services/api/scripts';
import { settingsTemplatesApi } from '@/services/api/settings-templates';
import { showErrorToast, showSuccessToast } from '@/utils/error-handler';
import { useScriptStore } from '@/store/scriptStore';
import type { DatasetMetadata, SessionConfig } from '@/types/metadata';
import type { SettingsTemplate } from '@/types/settings-template';
import { SettingsTemplateType } from '@/types/settings-template';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface MetadataConfigModalProps {
  scriptId: string;
  currentMetadata?: DatasetMetadata | null;
  onMetadataUpdated?: (metadata: DatasetMetadata) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function MetadataConfigModal({
  scriptId,
  currentMetadata,
  onMetadataUpdated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: MetadataConfigModalProps) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (controlledOnOpenChange || (() => {})) : setInternalOpen;
  const [isSaving, setIsSaving] = useState(false);
  
  // Template state
  const [templates, setTemplates] = useState<SettingsTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  
  // Save as Template dialog state
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [templateAction, setTemplateAction] = useState<'create' | 'update'>('create');
  const [newTemplateName, setNewTemplateName] = useState<string>('');
  const [selectedTemplateToUpdate, setSelectedTemplateToUpdate] = useState<string>('');
  
  // Form state
  const [timezone, setTimezone] = useState<string>('UTC');
  const [sessionStrategy, setSessionStrategy] = useState<'by_date' | 'trading_sessions'>('by_date');
  const [sessions, setSessions] = useState<SessionConfig[]>([]);
  const [intervalIdFormat, setIntervalIdFormat] = useState<'timestamp' | 'datetime'>('timestamp');
  const [intervalDuration, setIntervalDuration] = useState<string>('');
  const [customIntervalDuration, setCustomIntervalDuration] = useState<string>('');
  const [showCustomInterval, setShowCustomInterval] = useState(false);
  const [showPresetMenu, setShowPresetMenu] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const presetMenuRef = useRef<HTMLDivElement>(null);

  // Close preset menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (presetMenuRef.current && !presetMenuRef.current.contains(event.target as Node)) {
        setShowPresetMenu(false);
      }
    };

    if (showPresetMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPresetMenu]);

  // Load global session config templates when modal opens
  useEffect(() => {
    if (open) {
      setIsLoadingTemplates(true);
      settingsTemplatesApi.list({ type: SettingsTemplateType.SESSION_CONFIG })
        .then((loadedTemplates) => {
          // Only show global templates (scriptId is null or undefined)
          const globalTemplates = loadedTemplates.filter(t => !t.scriptId);
          setTemplates(globalTemplates);
        })
        .catch((error) => {
          console.error('Failed to load templates:', error);
          setTemplates([]);
        })
        .finally(() => {
          setIsLoadingTemplates(false);
        });
    } else {
      // Reset template selection when modal closes
      setSelectedTemplateId('');
    }
  }, [open]);

  // Load current metadata when modal opens (only if no template is selected)
  useEffect(() => {
    if (open && currentMetadata && !selectedTemplateId) {
      setTimezone(currentMetadata.timezone || 'UTC');
      // Convert 'custom' to 'trading_sessions' for UI consistency (they work the same)
      const strategy = currentMetadata.session_strategy === 'custom' 
        ? 'trading_sessions' 
        : (currentMetadata.session_strategy || 'by_date');
      setSessionStrategy(strategy as 'by_date' | 'trading_sessions');
      setSessions(currentMetadata.sessions || []);
      setIntervalIdFormat(currentMetadata.interval_id_format || 'timestamp');
      const durationValue = currentMetadata.interval_duration 
        ? (typeof currentMetadata.interval_duration === 'string' 
            ? currentMetadata.interval_duration 
            : String(currentMetadata.interval_duration))
        : '';
      setIntervalDuration(durationValue);
      
      // Check if duration is a custom value (not in predefined list)
      const predefinedValues = ['none', '30', '60', '90', '120', '240', '480', '1440', '90m', '1h', '4h'];
      if (durationValue && !predefinedValues.includes(durationValue)) {
        setCustomIntervalDuration(durationValue);
        setShowCustomInterval(true);
      } else {
        setCustomIntervalDuration('');
        setShowCustomInterval(false);
      }
    } else if (open && !selectedTemplateId) {
      // Reset to defaults
      setTimezone('UTC');
      setSessionStrategy('by_date');
      setSessions([]);
      setIntervalIdFormat('timestamp');
      setIntervalDuration('');
      setCustomIntervalDuration('');
      setShowCustomInterval(false);
    }
  }, [open, currentMetadata, selectedTemplateId]);

  // Load template configuration when selected
  useEffect(() => {
    if (open && selectedTemplateId) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template && template.config) {
        const config = template.config as any;
        
        if (config.timezone) setTimezone(config.timezone);
        if (config.session_strategy) {
          const strategy = config.session_strategy === 'custom' 
            ? 'trading_sessions' 
            : config.session_strategy;
          setSessionStrategy(strategy as 'by_date' | 'trading_sessions');
        }
        if (config.sessions) setSessions(config.sessions);
        if (config.interval_id_format) setIntervalIdFormat(config.interval_id_format);
        if (config.interval_duration) {
          const durationValue = typeof config.interval_duration === 'string' 
            ? config.interval_duration 
            : String(config.interval_duration);
          setIntervalDuration(durationValue);
          
          const predefinedValues = ['none', '30', '60', '90', '120', '240', '480', '1440', '90m', '1h', '4h'];
          if (durationValue && !predefinedValues.includes(durationValue)) {
            setCustomIntervalDuration(durationValue);
            setShowCustomInterval(true);
          } else {
            setCustomIntervalDuration('');
            setShowCustomInterval(false);
          }
        }
      }
    }
  }, [open, selectedTemplateId, templates]);

  const handleAddSession = () => {
    setSessions([
      ...sessions,
      {
        name: '',
        start_time: '00:00',
        end_time: '23:59',
        description: '',
      },
    ]);
  };

  const sessionPresets = {
    standard: [
      {
        name: 'premarket',
        start_time: '04:00',
        end_time: '09:30',
        description: 'Pre-market session',
      },
      {
        name: 'regular',
        start_time: '09:30',
        end_time: '16:00',
        description: 'Regular trading hours',
      },
      {
        name: 'afterhours',
        start_time: '16:00',
        end_time: '20:00',
        description: 'After-hours session',
      },
    ],
    futures: [
      {
        name: 'overnight',
        start_time: '18:00',
        end_time: '09:30',
        description: 'Overnight session (crosses midnight)',
      },
      {
        name: 'regular',
        start_time: '09:30',
        end_time: '16:00',
        description: 'Regular trading hours',
      },
      {
        name: 'afterhours',
        start_time: '16:00',
        end_time: '18:00',
        description: 'After-hours session',
      },
    ],
    forex: [
      {
        name: 'asian',
        start_time: '00:00',
        end_time: '09:00',
        description: 'Asian session',
      },
      {
        name: 'london',
        start_time: '08:00',
        end_time: '17:00',
        description: 'London session',
      },
      {
        name: 'newyork',
        start_time: '13:00',
        end_time: '22:00',
        description: 'New York session',
      },
    ],
  };

  const handleLoadPreset = (presetName: keyof typeof sessionPresets) => {
    setSessions(sessionPresets[presetName]);
  };

  const handleRemoveSession = (index: number) => {
    setSessions(sessions.filter((_, i) => i !== index));
  };

  const handleSessionChange = (index: number, field: keyof SessionConfig, value: string) => {
    const updated = [...sessions];
    updated[index] = { ...updated[index], [field]: value };
    setSessions(updated);
  };

  const buildMetadata = (): DatasetMetadata => {
    const metadata: DatasetMetadata = {
      timezone,
      session_strategy: sessionStrategy,
      interval_id_format: intervalIdFormat,
    };

    // Add sessions if using trading_sessions
    if (sessionStrategy === 'trading_sessions') {
      // Validate sessions if any are provided
      if (sessions.length > 0) {
        const validSessions = sessions.filter(s => s.name && s.start_time && s.end_time);
        if (validSessions.length > 0) {
          metadata.sessions = validSessions;
        }
      }
      // Sessions always use only the name (no date prefix) for grouping
      metadata.session_include_date = false;
    }

    // Add interval duration if provided
    if (intervalDuration.trim()) {
      // Try to parse as number, otherwise use as string
      const parsed = parseInt(intervalDuration);
      metadata.interval_duration = isNaN(parsed) ? intervalDuration : parsed;
    }

    return metadata;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const metadata = buildMetadata();

      // Validate sessions if using trading_sessions
      if (sessionStrategy === 'trading_sessions' && sessions.length > 0) {
        const validSessions = sessions.filter(s => s.name && s.start_time && s.end_time);
        if (validSessions.length === 0) {
          showErrorToast(
            new Error(t('scripts.atLeastOneSessionRequired') || 'At least one valid session is required'),
            t('scripts.validationError') || 'Validation Error'
          );
          setIsSaving(false);
          return;
        }
      }

      await scriptsApi.updateMetadata(scriptId, metadata);
      showSuccessToast(t('scripts.metadataUpdatedSuccess') || 'Metadata updated successfully');
      onMetadataUpdated?.(metadata);
      setOpen(false);
    } catch (error) {
      console.error('Failed to update metadata:', error);
      showErrorToast(error, t('scripts.metadataUpdateError') || 'Failed to update metadata');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save as template - opens dialog
  const handleSaveAsTemplate = () => {
    // Reset dialog state
    setTemplateAction('create');
    setNewTemplateName('');
    setSelectedTemplateToUpdate('');
    setSaveTemplateDialogOpen(true);
  };

  // Handle update current template (quick update without dialog)
  const handleUpdateCurrentTemplate = async () => {
    if (!selectedTemplateId) {
      showErrorToast('No template selected');
      return;
    }

    // Verify template exists before attempting update
    const currentTemplate = templates.find(t => t.id === selectedTemplateId);
    if (!currentTemplate) {
      showErrorToast('Template not found. It may have been deleted. Please reload templates.');
      setSelectedTemplateId('');
      try {
        const updatedTemplates = await settingsTemplatesApi.list({ 
          type: SettingsTemplateType.SESSION_CONFIG 
        });
        const globalTemplates = updatedTemplates.filter(t => !t.scriptId);
        setTemplates(globalTemplates);
      } catch (error) {
        console.error('Failed to refresh templates:', error);
      }
      return;
    }

    setIsSaving(true);
    try {
      const metadata = buildMetadata();

      await settingsTemplatesApi.update(selectedTemplateId, {
        config: metadata as Record<string, unknown>,
      });

      // Refresh templates list
      const updatedTemplates = await settingsTemplatesApi.list({ 
        type: SettingsTemplateType.SESSION_CONFIG 
      });
      const globalTemplates = updatedTemplates.filter(t => !t.scriptId);
      setTemplates(globalTemplates);
      
      showSuccessToast(`${currentTemplate.name} updated successfully`);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        showErrorToast('Template not found. It may have been deleted. Please select a different template.');
        setSelectedTemplateId('');
        try {
          const updatedTemplates = await settingsTemplatesApi.list({ 
            type: SettingsTemplateType.SESSION_CONFIG 
          });
          const globalTemplates = updatedTemplates.filter(t => !t.scriptId);
          setTemplates(globalTemplates);
        } catch (refreshError) {
          console.error('Failed to refresh templates:', refreshError);
        }
      } else {
        showErrorToast(error.message || 'Failed to update template');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle template save (create or update)
  const handleTemplateSave = async () => {
    try {
      const metadata = buildMetadata();

      if (templateAction === 'create') {
        if (!newTemplateName.trim()) {
          showErrorToast('Please enter a template name');
          return;
        }

      await settingsTemplatesApi.create({
        name: newTemplateName.trim(),
        type: SettingsTemplateType.SESSION_CONFIG,
        config: metadata as Record<string, unknown>,
        // Don't set scriptId - these are global templates
      });

        showSuccessToast('Template created successfully');
      } else {
        if (!selectedTemplateToUpdate) {
          showErrorToast('Please select a template to update');
          return;
        }

        await settingsTemplatesApi.update(selectedTemplateToUpdate, {
          config: metadata as Record<string, unknown>,
        });

        showSuccessToast('Template updated successfully');
      }

      // Refresh templates list
      const updatedTemplates = await settingsTemplatesApi.list({ 
        type: SettingsTemplateType.SESSION_CONFIG 
      });
      const globalTemplates = updatedTemplates.filter(t => !t.scriptId);
      setTemplates(globalTemplates);
      
      // Close dialog
      setSaveTemplateDialogOpen(false);
      setNewTemplateName('');
      setSelectedTemplateToUpdate('');
    } catch (error: any) {
      showErrorToast(error.message || `Failed to ${templateAction === 'create' ? 'create' : 'update'} template`);
    }
  };

  // Convert session config to YAML format for config block
  const convertSessionConfigToYAML = (): string => {
    const metadata = buildMetadata();
    
    // Build session config object for YAML
    const sessionConfig: any = {
      config: {},
    };

    // Add session fields to config
    if (metadata.timezone) {
      sessionConfig.config.timezone = metadata.timezone;
    }
    if (metadata.session_strategy) {
      sessionConfig.config.session_strategy = metadata.session_strategy;
    }
    if (metadata.interval_id_format) {
      sessionConfig.config.interval_id_format = metadata.interval_id_format;
    }
    if (metadata.interval_duration !== undefined && metadata.interval_duration !== null && metadata.interval_duration !== '') {
      sessionConfig.config.interval_duration = metadata.interval_duration;
    }
    if (metadata.sessions && metadata.sessions.length > 0) {
      sessionConfig.config.sessions = metadata.sessions;
    }

    // Use professional YAML library for robust stringification
    // The library should handle proper indentation for arrays (sessions) automatically
    const yamlString = stringifyYAML(sessionConfig, {
      indent: 2,
      lineWidth: 0, // No line wrapping
      simpleKeys: false,
      defaultStringType: 'PLAIN',
      defaultKeyType: 'PLAIN',
    });
    
    return yamlString;
  };

  // Handle save and update config block (only session section, preserve other sections)
  const handleSaveAndUpdateConfigBlock = async () => {
    // Validate sessions if using trading_sessions
    if (sessionStrategy === 'trading_sessions' && sessions.length > 0) {
      const validSessions = sessions.filter(s => s.name && s.start_time && s.end_time);
      if (validSessions.length === 0) {
        showErrorToast(
          new Error(t('scripts.atLeastOneSessionRequired') || 'At least one valid session is required'),
          t('scripts.validationError') || 'Validation Error'
        );
        return;
      }
    }

    setIsSaving(true);
    
    try {
      // First, save the configuration to the database
      const metadata = buildMetadata();
      await scriptsApi.updateMetadata(scriptId, metadata);
      
      // Update script in store if available
      const currentScript = useScriptStore.getState().currentScript;
      if (currentScript && currentScript.id === scriptId) {
        // Refresh script to get updated metadata
        try {
          const updatedScript = await scriptsApi.get(scriptId);
          if (updatedScript) {
            useScriptStore.getState().setCurrentScript(updatedScript);
          }
        } catch (error) {
          console.warn('Failed to refresh script in store:', error);
        }
      }

      // Now update the config block in the DSL script (only session section)
      const sessionYamlConfig = convertSessionConfigToYAML();
      
      // Get current script (use latest from store to ensure we have the most recent dslCode)
      const script = useScriptStore.getState().currentScript;
      if (!script) {
        showErrorToast('No script found');
        setIsSaving(false);
        return;
      }

      const latestDslCode = script.dslCode || '';

      // Find config block
      const configMatch = latestDslCode.match(/^config:\s*$/m);
      let newDslCode: string;
      
      if (configMatch) {
        // Config block exists - update only session section, preserve other sections
        const configStartIndex = configMatch.index!;
        const lines = latestDslCode.split('\n');
        const configStartLine = latestDslCode.substring(0, configStartIndex).split('\n').length - 1;
        
        // Parse existing config block to preserve other sections
        let configEndLine = configStartLine;
        let baseIndent = 0;
        let foundFirstContent = false;
        
        // Find where the config block ends
        // The block ends when we find a line with indent <= baseIndent (or indent 0) that's not part of the config
        for (let i = configStartLine + 1; i < lines.length; i++) {
          const line = lines[i];
          if (line === undefined) continue;
          
          if (line.trim() === '') {
            // Empty lines don't end the block, but we track them
            continue;
          }
          
          const indentMatch = line.match(/^(\s*)/);
          const indent = indentMatch?.[1]?.length || 0;
          
          // First non-empty line sets the base indent (should be 2 spaces for content under "config:")
          if (!foundFirstContent && line.trim() !== '') {
            if (indent === 0) {
              // Line with no indent means we've reached the end of the config block
              configEndLine = i - 1; // End before this line
              break;
            }
            baseIndent = indent;
            foundFirstContent = true;
            configEndLine = i;
            continue;
          }
          
          // If we find a line with indent < baseIndent (or indent 0), the block ends
          // This catches lines like "sessions:" with 0 indent that are outside the config block
          if (indent < baseIndent && line.trim() !== '') {
            // This line is outside the config block
            configEndLine = i - 1; // End before this line
            break;
          }
          
          // If indent equals baseIndent, check if it's a top-level key in the config
          // If it's a key that should be inside config (like "sessions:", "backtest:"), it's still part of the block
          // But if it's a key that appears to be outside (like a duplicate "sessions:" with wrong indent), we need to be careful
          if (indent === baseIndent && line.trim() !== '') {
            // This is still part of the config block
            configEndLine = i;
            continue;
          }
          
          // Still inside the config block (indent > baseIndent)
          configEndLine = i;
        }
        
        // Extract existing config block content
        const existingConfigLines = lines.slice(configStartLine + 1, configEndLine + 1);
        
        // Remove base indentation (2 spaces) from existing config YAML before parsing
        // This ensures the YAML parser can correctly interpret the structure
        const dedentedExistingConfigYaml = existingConfigLines
          .map(line => {
            if (line.trim() === '') {
              return line; // Preserve empty lines
            }
            // Remove 2 spaces of indentation (base indentation under "config:")
            if (line.startsWith('  ')) {
              return line.slice(2);
            }
            return line;
          })
          .join('\n');
        
        // Parse existing config to preserve other sections (like backtest:)
        let existingConfig: any = {};
        try {
          if (dedentedExistingConfigYaml.trim()) {
            existingConfig = parseYAML(dedentedExistingConfigYaml) || {};
          }
        } catch (error) {
          console.warn('Failed to parse existing config block, will replace entirely:', error);
          console.warn('YAML content:', dedentedExistingConfigYaml);
          existingConfig = {};
        }
        
        // Extract session config from the new YAML
        const sessionYamlLines = sessionYamlConfig.split('\n');
        let sessionYamlContent: string;
        
        if (sessionYamlLines[0] === 'config:') {
          sessionYamlContent = sessionYamlLines.slice(1).join('\n');
        } else {
          sessionYamlContent = sessionYamlConfig.replace(/^config:\s*\n/, '');
        }
        
        // Parse the new session config
        let newSessionConfig: any = {};
        try {
          if (sessionYamlContent.trim()) {
            newSessionConfig = parseYAML(sessionYamlContent) || {};
          }
        } catch (error) {
          console.error('Failed to parse new session config:', error);
          showErrorToast('Failed to generate session config YAML');
          setIsSaving(false);
          return;
        }
        
        // Merge: preserve existing config (like backtest:), update/add session fields
        // Deep merge to ensure nested objects are properly merged
        const mergedConfig = {
          ...existingConfig,
        };
        
        // Merge session fields specifically to ensure proper structure
        if (newSessionConfig.sessions) {
          mergedConfig.sessions = newSessionConfig.sessions;
        }
        if (newSessionConfig.timezone !== undefined) {
          mergedConfig.timezone = newSessionConfig.timezone;
        }
        if (newSessionConfig.session_strategy !== undefined) {
          mergedConfig.session_strategy = newSessionConfig.session_strategy;
        }
        if (newSessionConfig.interval_id_format !== undefined) {
          mergedConfig.interval_id_format = newSessionConfig.interval_id_format;
        }
        if (newSessionConfig.interval_duration !== undefined) {
          mergedConfig.interval_duration = newSessionConfig.interval_duration;
        }
        
        // Convert merged config back to YAML
        // The library should handle proper indentation for arrays (sessions) automatically
        const mergedYaml = stringifyYAML(mergedConfig, {
          indent: 2,
          lineWidth: 0,
          simpleKeys: false,
          defaultStringType: 'PLAIN',
          defaultKeyType: 'PLAIN',
        });
        
        // Indent the merged YAML content by 2 spaces (since it goes under "config:")
        // Split by lines and add 2 spaces to each non-empty line
        const mergedYamlLines = mergedYaml.split('\n');
        const indentedMergedYaml = mergedYamlLines
          .map(line => {
            if (line.trim() === '') {
              return line; // Preserve empty lines as-is
            }
            return '  ' + line; // Add 2 spaces indentation
          })
          .join('\n');
        
        // Reconstruct DSL code
        // beforeConfig: everything before "config:" line (including the line itself)
        const beforeConfig = lines.slice(0, configStartLine + 1).join('\n'); // Include "config:" line
        
        // afterConfig: everything after the config block, but filter out duplicate content
        // Look for lines that appear to be duplicates (like "sessions:" with wrong indentation)
        const afterConfigLines = lines.slice(configEndLine + 1);
        const filteredAfterConfig: string[] = [];
        let skipDuplicateBlock = false;
        let duplicateBlockIndent = -1;
        
        for (let i = 0; i < afterConfigLines.length; i++) {
          const line = afterConfigLines[i];
          if (line === undefined) continue;
          
          const trimmed = line.trim();
          const indentMatch = line.match(/^(\s*)/);
          const indent = indentMatch?.[1]?.length || 0;
          
          // Check if this line starts a duplicate config block
          if (!skipDuplicateBlock && (trimmed === 'sessions:' || trimmed === 'backtest:' || 
              trimmed === 'timezone:' || trimmed === 'session_strategy:' || 
              trimmed === 'interval_id_format:' || trimmed === 'interval_duration:')) {
            // This looks like a duplicate config key - check if it has wrong indentation
            if (indent <= 2) {
              // This is likely a duplicate - skip this block
              skipDuplicateBlock = true;
              duplicateBlockIndent = indent;
              continue; // Skip this line
            }
          }
          
          // If we're skipping a duplicate block, continue until we find the end
          if (skipDuplicateBlock) {
            // If we find a line with indent <= duplicateBlockIndent that's not a config key, end the duplicate block
            if (indent <= duplicateBlockIndent && trimmed !== '' && 
                !trimmed.match(/^(sessions|backtest|timezone|session_strategy|interval_id_format|interval_duration):/)) {
              skipDuplicateBlock = false;
              duplicateBlockIndent = -1;
              // Include this line (it's the start of real content after the duplicate)
              filteredAfterConfig.push(line);
            }
            // Otherwise, continue skipping
            continue;
          }
          
          // Normal line - include it
          filteredAfterConfig.push(line);
        }
        
        const afterConfig = filteredAfterConfig.join('\n');
        
        // Join: beforeConfig (includes "config:"), new indented content, afterConfig
        // Ensure proper line breaks
        const parts = [
          beforeConfig.trimEnd(), // Remove trailing newlines
          indentedMergedYaml, // Indented merged YAML content (preserves backtest:, updates/adds session fields)
          afterConfig.trimStart() // Remove leading newlines
        ].filter(part => part.length > 0);
        
        newDslCode = parts.join('\n');
      } else {
        // No config block exists - create it at the beginning with only session config
        newDslCode = sessionYamlConfig + '\n\n' + latestDslCode.trim();
      }
      
      // Update script
      const result = await scriptsApi.update(script.id, {
        dslCode: newDslCode,
      });

      // Update script in store
      if (result.script) {
        useScriptStore.getState().setCurrentScript(result.script);
      }
      
      showSuccessToast(t('scripts.metadataUpdatedSuccess') || 'Metadata updated successfully');
      onMetadataUpdated?.(metadata);
      setOpen(false);
    } catch (error: any) {
      console.error('Failed to save and update config block:', error);
      showErrorToast(error, t('scripts.metadataUpdateError') || 'Failed to update metadata');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <TooltipProvider>
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <Settings className="w-4 h-4 mr-2" />
            {t('scripts.sessionConfig') || 'Session Configuration'}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('scripts.sessionConfig') || 'Session Configuration'}</DialogTitle>
          <DialogDescription>
            {t('scripts.sessionConfigDescription') || 'Configure trading sessions and intervals for analysis'}
          </DialogDescription>
        </DialogHeader>

        {/* Template Selection */}
        <div className="space-y-2 border-b pb-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-semibold">
              {t('scripts.template') || 'Template'} ({t('scripts.global') || 'Global'})
            </Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-sm">
                  {t('scripts.templateHint') || 'Select a global template to load session configuration. Templates are shared across all scripts.'}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          {selectedTemplateId && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Info className="w-3 h-3" />
              {t('scripts.usingTemplate') || 'Using template'}: <strong>{templates.find(t => t.id === selectedTemplateId)?.name || 'Unknown'}</strong>
            </p>
          )}
        </div>

        <div className="space-y-6 py-4">
          {/* Timezone */}
          <div className="space-y-2">
            <Label htmlFor="timezone">{t('scripts.timezone') || 'Timezone'}</Label>
            <Select value={timezone} onValueChange={(value) => setTimezone(value)}>
              <SelectTrigger id="timezone">
                <SelectValue placeholder={t('scripts.selectTimezone') || 'Select timezone'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UTC">UTC (Coordinated Universal Time)</SelectItem>
                <SelectItem value="America/New_York">America/New_York (EST/EDT)</SelectItem>
                <SelectItem value="America/Chicago">America/Chicago (CST/CDT)</SelectItem>
                <SelectItem value="America/Denver">America/Denver (MST/MDT)</SelectItem>
                <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</SelectItem>
                <SelectItem value="Europe/London">Europe/London (GMT/BST)</SelectItem>
                <SelectItem value="Europe/Paris">Europe/Paris (CET/CEST)</SelectItem>
                <SelectItem value="Europe/Berlin">Europe/Berlin (CET/CEST)</SelectItem>
                <SelectItem value="Asia/Tokyo">Asia/Tokyo (JST)</SelectItem>
                <SelectItem value="Asia/Shanghai">Asia/Shanghai (CST)</SelectItem>
                <SelectItem value="Asia/Hong_Kong">Asia/Hong_Kong (HKT)</SelectItem>
                <SelectItem value="Australia/Sydney">Australia/Sydney (AEDT/AEST)</SelectItem>
                <SelectItem value="America/Sao_Paulo">America/Sao_Paulo (BRT/BRST)</SelectItem>
                <SelectItem value="America/Mexico_City">America/Mexico_City (CST/CDT)</SelectItem>
                <SelectItem value="America/Toronto">America/Toronto (EST/EDT)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('scripts.timezoneHint') || 'Timezone used for calculating sessions and intervals'}
            </p>
          </div>

          {/* Session Strategy */}
          <div className="space-y-2">
            <Label htmlFor="sessionStrategy">{t('scripts.sessionStrategy') || 'Session Strategy'}</Label>
            <Select value={sessionStrategy} onValueChange={(v: any) => setSessionStrategy(v)}>
              <SelectTrigger id="sessionStrategy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="by_date">
                  <div>
                    <div className="font-medium">{t('scripts.sessionStrategyByDate') || 'By Date'}</div>
                    <div className="text-xs text-muted-foreground">{t('scripts.sessionStrategyByDateDesc') || 'One session per day (default)'}</div>
                  </div>
                </SelectItem>
                <SelectItem value="trading_sessions">
                  <div>
                    <div className="font-medium">{t('scripts.sessionStrategyTrading') || 'Trading Sessions'}</div>
                    <div className="text-xs text-muted-foreground">{t('scripts.sessionStrategyTradingDesc') || 'Define custom trading sessions (premarket, regular, afterhours, etc.)'}</div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <div className="bg-muted/50 p-3 rounded-md space-y-1">
              <p className="text-xs font-medium">{t('scripts.sessionStrategyHowItWorks') || 'How it works:'}</p>
              <p className="text-xs text-muted-foreground">
                {sessionStrategy === 'by_date' 
                  ? t('scripts.sessionStrategyByDateHint') || 'Groups all data from the same calendar day into one session. Example: All data from 2025-01-15 belongs to session "2025-01-15". Simple and works for most cases.'
                  : t('scripts.sessionStrategyTradingHint') || 'Groups data into multiple sessions per day based on time ranges. Example: Data from 04:00-09:30 → "premarket", 09:30-16:00 → "regular", 16:00-20:00 → "afterhours". Useful for analyzing different trading periods separately.'
                }
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {sessionStrategy === 'by_date'
                  ? t('scripts.sessionStrategyByDateUseCase') || 'Use this when you want to analyze data day by day, regardless of time of day.'
                  : t('scripts.sessionStrategyTradingUseCase') || 'Use this when you need to analyze pre-market, regular trading hours, and after-hours separately.'
                }
              </p>
            </div>
          </div>

          {/* Sessions Configuration */}
          {sessionStrategy === 'trading_sessions' && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-3 rounded-md">
                <p className="text-xs text-muted-foreground">
                  {t('scripts.sessionGroupingInfo') || 'Sessions will be grouped by type only (e.g., "premarket", "regular", "afterhours") without date prefix. All sessions of the same type across all dates will be grouped together.'}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('scripts.sessionGroupingExample') || 'Example: All "premarket" sessions from different dates will be grouped as "premarket". Useful for analyzing patterns across all trading days.'}
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <Label>{t('scripts.sessions') || 'Sessions'}</Label>
                <div className="flex gap-2">
                  <div className="relative" ref={presetMenuRef}>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPresetMenu(!showPresetMenu)}
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      {t('scripts.loadPreset') || 'Load Preset'}
                    </Button>
                    {showPresetMenu && (
                      <div className="absolute right-0 top-full mt-1 bg-popover border rounded-md shadow-lg z-50 min-w-[200px]">
                        <div className="p-1">
                          <button
                            type="button"
                            onClick={() => {
                              handleLoadPreset('standard')
                              setShowPresetMenu(false)
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm"
                          >
                            {t('scripts.presetStandard') || 'Standard Trading'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              handleLoadPreset('futures')
                              setShowPresetMenu(false)
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm"
                          >
                            {t('scripts.presetFutures') || 'Futures (Overnight)'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              handleLoadPreset('forex')
                              setShowPresetMenu(false)
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm"
                          >
                            {t('scripts.presetForex') || 'Forex Sessions'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddSession}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('scripts.addSession') || 'Add Session'}
                  </Button>
                </div>
              </div>

              {sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('scripts.noSessions') || 'No sessions configured. Click "Add Session" to create one or load a preset.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {sessions.map((session, index) => (
                    <div key={index} className="border rounded-md p-2.5 bg-muted/30">
                      <div className="flex items-center gap-2">
                        {/* Name */}
                        <div className="flex-1 min-w-[120px]">
                          <Input
                            id={`session-name-${index}`}
                            value={session.name}
                            onChange={(e) => handleSessionChange(index, 'name', e.target.value)}
                            placeholder={t('scripts.namePlaceholder') || 'Name'}
                            className="h-8 text-sm"
                          />
                        </div>
                        
                        {/* Start Time */}
                        <div className="w-[100px]">
                          <Input
                            id={`session-start-${index}`}
                            type="time"
                            value={session.start_time}
                            onChange={(e) => handleSessionChange(index, 'start_time', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        
                        <span className="text-xs text-muted-foreground">→</span>
                        
                        {/* End Time */}
                        <div className="w-[100px]">
                          <Input
                            id={`session-end-${index}`}
                            type="time"
                            value={session.end_time}
                            onChange={(e) => handleSessionChange(index, 'end_time', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        
                        {/* Description (optional, smaller) */}
                        <div className="flex-1 min-w-[150px]">
                          <Input
                            id={`session-description-${index}`}
                            value={session.description || ''}
                            onChange={(e) => handleSessionChange(index, 'description', e.target.value)}
                            placeholder={t('scripts.descriptionPlaceholder') || 'Description (optional)'}
                            className="h-8 text-sm"
                          />
                        </div>
                        
                        {/* Delete button */}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveSession(index)}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Advanced Options */}
          <div className="border-t pt-4">
            <button
              type="button"
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center gap-2">
                <Label className="text-base font-semibold cursor-pointer">
                  {t('scripts.advancedOptions') || 'Advanced Options'}
                </Label>
                <Info className="w-4 h-4 text-muted-foreground" />
              </div>
              {showAdvancedOptions ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
            <p className="text-xs text-muted-foreground mt-1 mb-4">
              {t('scripts.advancedOptionsDescription') || 'Configure advanced interval settings for structural columns'}
            </p>

            {showAdvancedOptions && (
              <div className="space-y-6 pt-2">
                {/* Interval ID Format */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="intervalIdFormat">{t('scripts.intervalIdFormat') || 'Interval ID Format'}</Label>
                    <Info className="w-3 h-3 text-muted-foreground" />
                  </div>
                  <Select value={intervalIdFormat} onValueChange={(v: any) => setIntervalIdFormat(v)}>
                    <SelectTrigger id="intervalIdFormat">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="timestamp">{t('scripts.intervalFormatTimestamp') || 'Timestamp'}</SelectItem>
                      <SelectItem value="datetime">{t('scripts.intervalFormatDatetime') || 'Datetime'}</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="bg-muted/50 p-3 rounded-md space-y-1">
                    <p className="text-xs font-medium">{t('scripts.intervalIdFormatTitle') || 'What is Interval ID?'}</p>
                    <p className="text-xs text-muted-foreground">
                      {intervalIdFormat === 'timestamp' 
                        ? t('scripts.intervalIdFormatTimestampDesc') || 'Interval ID will be a Unix timestamp (number). Example: 1754258400'
                        : t('scripts.intervalIdFormatDatetimeDesc') || 'Interval ID will be a datetime string. Example: "2025-08-03 10:00:00"'
                      }
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('scripts.intervalIdFormatUsage') || 'Used for grouping and identifying intervals in your analysis. Default: Timestamp'}
                    </p>
                  </div>
                </div>

                {/* Interval Duration */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="intervalDuration">
                      {t('scripts.intervalDuration') || 'Interval Duration'} ({t('common.optional') || 'Optional'})
                    </Label>
                    <Info className="w-3 h-3 text-muted-foreground" />
                  </div>
                  <Select 
                    value={showCustomInterval ? 'custom' : (intervalDuration || 'none')} 
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setIntervalDuration('')
                        setShowCustomInterval(false)
                        setCustomIntervalDuration('')
                      } else if (value === 'custom') {
                        setShowCustomInterval(true)
                        if (customIntervalDuration) {
                          setIntervalDuration(customIntervalDuration)
                        }
                      } else {
                        setIntervalDuration(value)
                        setShowCustomInterval(false)
                        setCustomIntervalDuration('')
                      }
                    }}
                  >
                    <SelectTrigger id="intervalDuration">
                      <SelectValue placeholder={t('scripts.selectIntervalDuration') || 'Select interval duration'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('scripts.intervalDurationNone') || 'None'}</SelectItem>
                      <SelectItem value="30">{t('scripts.intervalDuration30min') || '30 minutes'}</SelectItem>
                      <SelectItem value="60">{t('scripts.intervalDuration1h') || '1 hour'}</SelectItem>
                      <SelectItem value="90">{t('scripts.intervalDuration90min') || '90 minutes'}</SelectItem>
                      <SelectItem value="120">{t('scripts.intervalDuration2h') || '2 hours'}</SelectItem>
                      <SelectItem value="240">{t('scripts.intervalDuration4h') || '4 hours'}</SelectItem>
                      <SelectItem value="480">{t('scripts.intervalDuration8h') || '8 hours'}</SelectItem>
                      <SelectItem value="1440">{t('scripts.intervalDuration1d') || '1 day'}</SelectItem>
                      <SelectItem value="custom">{t('scripts.intervalDurationCustom') || 'Custom...'}</SelectItem>
                    </SelectContent>
                  </Select>
                  {showCustomInterval && (
                    <div className="mt-2 space-y-2">
                      <Input
                        value={customIntervalDuration}
                        onChange={(e) => {
                          const value = e.target.value
                          setCustomIntervalDuration(value)
                          setIntervalDuration(value)
                        }}
                        placeholder={t('scripts.intervalDurationCustomHint') || 'e.g., 45, 90m, 1h, 2h, 4h'}
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('scripts.intervalDurationCustomHint') || 'Enter a number (minutes) or a string with unit: "90m" (minutes), "1h" (hour), "4h" (hours)'}
                      </p>
                    </div>
                  )}
                  <div className="bg-muted/50 p-3 rounded-md space-y-1">
                    <p className="text-xs font-medium">{t('scripts.intervalDurationTitle') || 'What is Interval Duration?'}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('scripts.intervalDurationDesc') || 'Defines how data is grouped into intervals. If set, intervals will be calculated based on this duration. If not set (None), each data point is its own interval.'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('scripts.intervalDurationExample') || 'Example: With 90 minutes duration, data is grouped into 90-minute intervals. Useful for analyzing patterns over fixed time periods.'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      <strong>{t('scripts.intervalDurationFormatTitle') || 'Accepted formats:'}</strong>
                    </p>
                    <ul className="text-xs text-muted-foreground mt-1 ml-4 list-disc space-y-1">
                      <li>{t('scripts.intervalDurationFormatNumber') || 'Number (minutes): 30, 60, 90, 120, 240'}</li>
                      <li>{t('scripts.intervalDurationFormatString') || 'String with unit: "30m", "1h", "90m", "2h", "4h"'}</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('scripts.intervalDurationDefault') || 'Default: None (no grouping by duration)'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline"
                  disabled={isLoadingTemplates}
                  className="min-w-[160px]"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isLoadingTemplates ? t('common.loading') || 'Loading...' : selectedTemplateId ? t('scripts.changeTemplate') || 'Change Template' : t('scripts.loadTemplate') || 'Load Template'}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {templates.length === 0 ? (
                  <DropdownMenuItem disabled>
                    <span className="text-muted-foreground">{t('scripts.noTemplatesAvailable') || 'No templates available'}</span>
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem
                      onClick={() => {
                        setSelectedTemplateId('');
                      }}
                    >
                      <span>{t('scripts.noneUseCustom') || 'None (use custom config)'}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {templates.map((template) => (
                      <DropdownMenuItem
                        key={template.id}
                        onClick={() => {
                          setSelectedTemplateId(template.id);
                        }}
                      >
                        <span>{template.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  disabled={isSaving}
                  className="min-w-[140px]"
                >
                  {isSaving ? (
                    t('common.saving') || 'Saving...'
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('common.save') || 'Save'}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem 
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <Save className="w-4 h-4 mr-2" />
                  <span>{t('scripts.saveCustomConfiguration') || 'Save Custom Configuration'}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{t('common.default') || 'Default'}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {selectedTemplateId && (
                  <>
                    <DropdownMenuItem 
                      onClick={handleUpdateCurrentTemplate}
                      disabled={isSaving}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      <span>{t('scripts.updateCurrentTemplate') || 'Update Current Template'}</span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {templates.find(t => t.id === selectedTemplateId)?.name || t('scripts.template') || 'Template'}
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem 
                  onClick={handleSaveAsTemplate}
                  disabled={isSaving}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  <span>{t('scripts.saveAsTemplate') || 'Save as Template'}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{t('scripts.reusable') || 'Reusable'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleSaveAndUpdateConfigBlock}
                  disabled={isSaving}
                >
                  <Code className="w-4 h-4 mr-2" />
                  <span>{t('scripts.saveAndUpdateScriptConfigBlock') || 'Save & Update Script Config Block'}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{t('scripts.code') || 'CODE'}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Save as Template Dialog */}
    <Dialog open={saveTemplateDialogOpen} onOpenChange={setSaveTemplateDialogOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('scripts.saveAsTemplate') || 'Save as Template'}</DialogTitle>
          <DialogDescription>
            {t('scripts.saveAsTemplateDescription') || 'Create a new template or update an existing one with the current configuration.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Action Selection */}
          <div className="space-y-3">
            <Label>{t('scripts.action') || 'Action'}</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="action-create"
                  name="templateAction"
                  value="create"
                  checked={templateAction === 'create'}
                  onChange={() => {
                    setTemplateAction('create');
                    setSelectedTemplateToUpdate('');
                  }}
                  className="w-4 h-4"
                />
                <Label htmlFor="action-create" className="font-normal cursor-pointer">
                  {t('scripts.createNewTemplate') || 'Create new template'}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="action-update"
                  name="templateAction"
                  value="update"
                  checked={templateAction === 'update'}
                  onChange={() => {
                    setTemplateAction('update');
                    setNewTemplateName('');
                  }}
                  className="w-4 h-4"
                />
                <Label htmlFor="action-update" className="font-normal cursor-pointer">
                  {t('scripts.updateExistingTemplate') || 'Update existing template'}
                </Label>
              </div>
            </div>
          </div>

          {/* Create new template */}
          {templateAction === 'create' && (
            <div className="space-y-2">
              <Label htmlFor="newTemplateName">{t('scripts.templateName') || 'Template Name'}</Label>
              <Input
                id="newTemplateName"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder={t('scripts.enterTemplateName') || 'Enter template name'}
                autoFocus
              />
            </div>
          )}

          {/* Update existing template */}
          {templateAction === 'update' && (
            <div className="space-y-2">
              <Label htmlFor="templateToUpdate">{t('scripts.selectTemplate') || 'Select Template'}</Label>
              <Select
                value={selectedTemplateToUpdate}
                onValueChange={setSelectedTemplateToUpdate}
              >
                <SelectTrigger id="templateToUpdate">
                  <SelectValue placeholder={t('scripts.selectTemplateToUpdate') || 'Select a template to update'} />
                </SelectTrigger>
                <SelectContent>
                  {templates.length === 0 ? (
                    <SelectItem value="__empty__" disabled>
                      {t('scripts.noTemplatesAvailable') || 'No templates available'}
                    </SelectItem>
                  ) : (
                    templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {templates.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {t('scripts.noTemplatesFound') || 'No templates found. Create a new template instead.'}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setSaveTemplateDialogOpen(false);
              setNewTemplateName('');
              setSelectedTemplateToUpdate('');
            }}
          >
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleTemplateSave}
            disabled={
              (templateAction === 'create' && !newTemplateName.trim()) ||
              (templateAction === 'update' && !selectedTemplateToUpdate)
            }
          >
            {templateAction === 'create' ? t('scripts.create') || 'Create' : t('scripts.update') || 'Update'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </TooltipProvider>
  );
}
